import intl from 'components/i18n/ReactIntlWrapper';

// ── Response shapes from BaseApiController ────────────────────────────────────
//
// 400  ASP.NET model validation  { title, errors: { field: [msgs] } }    (no data/errorLabelKey)
// 401  AuthorizePermission        empty body
// 401  AuthorizationException     { data: null, errors: [{errorLabelKey, errorMessage}] }
// 404  ObjectNotFoundException    { data: null, errors: [{errorLabelKey, errorMessage, errorCode}] }
// 406  BusinessException          { data: null, errors: [{errorLabelKey, errorMessage}] }
// 500  unhandled Exception        ASP.NET ProblemDetails { type, title, status, detail }
//
// ─────────────────────────────────────────────────────────────────────────────

function getApiErrors(data: any): { errorLabelKey?: string; errorMessage?: string }[] | null {
    return Array.isArray(data?.errors) && data.errors.length > 0 ? data.errors : null;
}

export function handleAPIError(error: any, toast: any, errors: any) {
    const data = error.response?.data;

    switch (error.response?.status) {
        case 400: {
            const apiErrors = getApiErrors(data);
            if (apiErrors) {
                // Custom format: [{errorLabelKey, errorMessage}]
                const msgs = apiErrors.map(e => intl.get(e.errorLabelKey) || e.errorMessage || '');
                if (errors && typeof errors === 'object') errors.summary = msgs;
                toast.error({ header: intl.get('error'), body: msgs.join(', ') || 'Validation error.' });
            } else if (data?.errors && typeof data.errors === 'object') {
                // ASP.NET ModelState: { field: [msg1, msg2] }
                const msgs = (Object.values(data.errors) as string[][]).flat();
                if (errors && typeof errors === 'object') errors.summary = msgs;
                toast.error({ header: intl.get('error'), body: data.title || msgs[0] || 'Validation error.' });
            } else {
                const msg = data?.title || data?.message || error?.message || 'Bad request.';
                if (errors && typeof errors === 'object') errors.summary = [msg];
                toast.error({ header: intl.get('error'), body: msg });
            }
            break;
        }

        case 401: {
            // Either empty body (AuthorizePermission) or { errors: [...] } (AuthorizationException)
            const apiErrors = getApiErrors(data);
            const msg = apiErrors
                ? (intl.get(apiErrors[0]?.errorLabelKey) || apiErrors[0]?.errorMessage)
                : (intl.get('error.unauthorized') || 'Unauthorized.');
            toast.error({ header: intl.get('error'), body: msg });
            break;
        }

        case 404: {
            // ObjectNotFoundException always returns { errors: [{errorLabelKey, ...}] }
            // but a gateway/nginx 404 may return empty body or HTML
            const apiErrors = getApiErrors(data);
            if (apiErrors) {
                apiErrors.forEach(e => {
                    toast.error({ header: intl.get('error'), body: intl.get(e.errorLabelKey) || e.errorMessage });
                });
            } else {
                const msg = (typeof data === 'string' && data.length < 200 ? data : null)
                    || intl.get('error.notfound')
                    || 'Not found.';
                toast.error({ header: intl.get('error'), body: msg });
            }
            break;
        }

        case 406: {
            // BusinessException / ValidationException: { errors: [{errorLabelKey, errorMessage}] }
            const apiErrors = getApiErrors(data);
            const msgs = apiErrors?.map(e => intl.get(e.errorLabelKey) || e.errorMessage || '') ?? [];
            if (errors && typeof errors === 'object') errors.summary = msgs;
            toast.error({ header: intl.get('error'), body: msgs.join(', ') || 'Validation error.' });
            break;
        }

        case 500: {
            // ASP.NET ProblemDetails: { title, detail } — no data/errors fields
            const msg = data?.detail || data?.title || error?.message || 'An unexpected error occurred.';
            toast.error({ header: intl.get('error'), body: msg });
            break;
        }

        default: {
            // 409 conflict, 403 forbidden, network errors, etc.
            const apiErrors = getApiErrors(data);
            const msg = apiErrors
                ? (intl.get(apiErrors[0]?.errorLabelKey) || apiErrors[0]?.errorMessage)
                : (data?.title || data?.message || error?.message || 'An unexpected error occurred.');
            toast.error({ header: intl.get('error'), body: msg });
            break;
        }
    }
}
